import { LeafletPixiContext } from "./useLeafletPixi";
import { useEffect, useState } from "react";
import { useTypedSelector } from "@hooks/useTypedSelector";
import L, { ControlOptions, TileLayerOptions } from "leaflet";
import Logger from "@utils/logger";
import st from './useWeatherLayers.module.scss'
import ReactDOMServer from "react-dom/server";
import { groupByProp } from "@utils/utils";
import { array, set, string } from "fp-ts";
import { fromEquals } from "fp-ts/es6/Eq";

const images = importAll(require.context('@images/weather/owm_legend/', false, /\.(png|jpe?g|svg)$/))

// @ts-ignore
function importAll(require) {
    return Object.fromEntries(require.keys().map((k: string) => [k.replace("./", ""), require(k).default ]));
}

const log = Logger.getLogger("weather")

interface WeatherLayerElement {
    id: string,
    legendImageURL?: string
    title: string,
}

interface OWMOptions extends TileLayerOptions {
    appId: string
    maxZoom: number
    legendOptions?: OWMLegendControlOptions
}

interface OWMLegendControlOptions extends ControlOptions {
}


export const WeatherElements: WeatherLayerElement[] = [
    {
        "title": "Температура воздуха",
        "id": "TA2",
        "legendImageURL": "temperature.png"
    },
    {
        "title": "Конвективные осадки",
        "id": "PAC0",
        "legendImageURL": "prec.png"
    },
    {
        "title": "Интенсивность осадков",
        "id": "PR0",
        "legendImageURL": "prec.png"
    },
    {
        "title": "Накопленные осадки",
        "id": "PA0",
        "legendImageURL": "prec.png"
    },
    {
        "title": "Накопленные осадки - дождь",
        "id": "PAR0",
        "legendImageURL": "prec.png"
    },
    {
        "title": "Накопленные осадки - снег",
        "id": "PAS0",
        "legendImageURL": "prec.png"
    },
    {
        "title": "Глубина снега",
        "id": "SD0",
        "legendImageURL": "snow.png"
    },
    {
        "title": "Облачность",
        "id": "CL",
        "legendImageURL": "cloud.png"
    },
    {
        "title": "Направление ветра",
        "id": "WND",
        "legendImageURL": "wind.png"
    },
    {
        "title": "Атмосферное давление",
        "id": "APM",
        "legendImageURL": "pressure.png"
    },
    {
        "title": "Относительная влажность воздуха",
        "id": "HRD0"
    }
].map(i =>
    ({ ...i, legendImageURL: i.legendImageURL ? images[i.legendImageURL] : undefined }))

const OwmLegend = ({ items }: { items: WeatherLayerElement[] }) => {
    const imageSet: Set<WeatherLayerElement> = set.fromArray(fromEquals<WeatherLayerElement>((a, b) =>
        string.Eq.equals(a.legendImageURL!, b.legendImageURL!)))(items)
    return <div className={st.owm_legend}>{Array.from(imageSet).map(i => <img key={i.id} src={i.legendImageURL} alt="none"/>)}</div>
}

const createOWMLayer = (options: OWMOptions) => (element: WeatherLayerElement) => {
    log.debug('Create OWM layer - ' + element.id, options)
    return new OWMLegendLayer(`//maps.openweathermap.org/maps/2.0/weather/${element.id}/{z}/{x}/{y}?appId=${options.appId}`, {
        ...options,
        id: element.id
    }, element)
}

class OWMLegendLayer extends L.TileLayer {
    owmOptions: OWMOptions
    owmId: string
    element: WeatherLayerElement

    constructor(urlTemplate: string, options: OWMOptions, element: WeatherLayerElement) {
        super(urlTemplate, options);
        this.owmOptions = options
        this.owmId = options.id!
        this.element = element
    }

    override onAdd(map: L.Map & { legendOwmControl: OWMLegendControl }): this {
        return super.onAdd(map);
    }
}

class OWMLegendControl extends L.Control {
    _container: HTMLElement

    constructor(options: ControlOptions) {
        super(options);
        this._container = L.DomUtil.create('div', 'owm_legend_container')
    }

    override onAdd(map: L.Map): HTMLElement {
        return this._container
    }

    updateLegened(elements: WeatherLayerElement[]) {
        this._container.innerHTML = ReactDOMServer.renderToString(<OwmLegend items={elements}/>)
    }
}

const owmLayerFactory = createOWMLayer({
    appId: '49b234cf1c354ef3584567e6223d6b21',
    maxZoom: 13
})

const weatherElementsById = groupByProp<WeatherLayerElement>('id')(WeatherElements)

export default function (pixiContext: LeafletPixiContext | undefined) {
    const { mapWeather } = useTypedSelector(state => state.mapTabSettings)
    const [layers, setCurrentOWMLayers] = useState<OWMLegendLayer[]>([])

    useEffect(() => {
        if (pixiContext) {
            let map = pixiContext.map as L.Map & { owmLegendControl?: OWMLegendControl }
            if (mapWeather.enabled) {
                if (!map.owmLegendControl) {
                    map.owmLegendControl = new OWMLegendControl({ position: "bottomleft" })
                    map.addControl(map.owmLegendControl)
                }
                const currentLayers = mapWeather.current

                const previousLayers = groupByProp<OWMLegendLayer>('owmId')(layers)
                const previousLayersKeys = Object.keys(previousLayers)

                const differenceToRemove = array.difference(string.Eq)(previousLayersKeys, currentLayers)
                differenceToRemove.forEach(i => map.removeLayer(previousLayers[i]))

                const differenceToAdd = array.difference(string.Eq)(currentLayers, previousLayersKeys)
                const addedLayers =  differenceToAdd.map(i => owmLayerFactory(weatherElementsById[i]))
                addedLayers.forEach(l => map.addLayer(l))

                map.owmLegendControl.updateLegened(currentLayers.flatMap(i => weatherElementsById[i].legendImageURL ? weatherElementsById[i] : []))

                setCurrentOWMLayers(state => {
                    return state.filter(i => differenceToRemove.indexOf(i.owmId) === -1).concat(addedLayers)
                })
            } else {
                if (map.owmLegendControl) {
                    map.removeControl(map.owmLegendControl)
                    delete map.owmLegendControl
                }
                layers.forEach(i => map.removeLayer(i))
                setCurrentOWMLayers([])
            }
        }
    }, [pixiContext, mapWeather.enabled, mapWeather.current])
}